import React from 'react'
import {Dialog, ToggleSwitch} from '@primer/react'
import styles from './CustomizationDialog.module.css'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'

export const CHART_CARD_CUSTOMIZATION_SETTINGS = {
  chartCardIncreasedContrast: 'chart-card-increased-contrast',
  chartCardHasLineStyle: 'chart-card-line-style',
  chartCardLineDataLabels: 'chart-card-line-data-labels',
  chartCardColumnDataLabels: 'chart-card-column-data-labels',
}

const DialogContentBody: React.FC<{type: 'LineChart' | 'ColumnChart'}> = ({type}) => {
  const [chartCardColumnDataLabels, setChartCardColumnDataLabels] = useLocalStorage(
    CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardColumnDataLabels,
    false,
  )
  const [chartCardIncreasedContrast, setChartCardIncreasedContrast] = useLocalStorage(
    CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardIncreasedContrast,
    false,
  )
  const [chartCardHasLineStyle, setChartCardHasLineStyle] = useLocalStorage(
    CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardHasLineStyle,
    true,
  )
  const [chartCardLineDataLabels, setChartCardLineDataLabels] = useLocalStorage(
    CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardLineDataLabels,
    false,
  )
  if (type === 'ColumnChart') {
    return (
      <div className={styles.ChartCardDialogBody}>
        <div className={styles.ChartCardDialogRow}>
          <div className={styles.ChartCardDialogColGrow}>
            <h2 className={styles.ChartCardDialogSwitchLabel} id="switchLabelIncreaseContrast">
              Increase Contrast
            </h2>
            <span className={styles.ChartCardDialogSwitchCaption} id="switchCaptionIncreaseContrast">
              Adds gradients and outlines to increase contrast in charts.
            </span>
          </div>
          <ToggleSwitch
            size="small"
            aria-labelledby="switchLabelIncreaseContrast"
            aria-describedby="switchCaptionIncreaseContrast"
            checked={chartCardIncreasedContrast}
            onClick={() => {
              setChartCardIncreasedContrast(!chartCardIncreasedContrast)
            }}
          />
        </div>
        <div className={styles.ChartCardDialogRow}>
          <div className={styles.ChartCardDialogColGrow}>
            <h2 className={styles.ChartCardDialogSwitchLabel} id="switchLabelColumnDataLabels">
              Show data labels
            </h2>
            <span className={styles.ChartCardDialogSwitchCaption} id="switchCaptionColumnDataLabels">
              Show labels for each data point in column and bar charts.
            </span>
          </div>
          <ToggleSwitch
            size="small"
            aria-labelledby="switchLabelColumnDataLabels"
            aria-describedby="switchCaptionColumnDataLabels"
            checked={chartCardColumnDataLabels}
            onClick={() => {
              setChartCardColumnDataLabels(!chartCardColumnDataLabels)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.ChartCardDialogBody}>
      <div className={styles.ChartCardDialogRow}>
        <div className={styles.ChartCardDialogColGrow}>
          <h2 className={styles.ChartCardDialogSwitchLabel} id="switchLabelLineStyle">
            Differentiate by line style
          </h2>
          <span className={styles.ChartCardDialogSwitchCaption} id="switchCaptionLineStyle">
            Use unique line styles (dashed, dotted, etc.) to differentiate lines in charts.
          </span>
        </div>
        <ToggleSwitch
          size="small"
          aria-labelledby="switchLabelLineStyle"
          aria-describedby="switchCaptionLineStyle"
          checked={chartCardHasLineStyle}
          onClick={() => {
            setChartCardHasLineStyle(!chartCardHasLineStyle)
          }}
        />
      </div>
      <div className={styles.ChartCardDialogRow}>
        <div className={styles.ChartCardDialogColGrow}>
          <h2 className={styles.ChartCardDialogSwitchLabel} id="switchLabelLineDataLabels">
            Show data labels
          </h2>
          <span className={styles.ChartCardDialogSwitchCaption} id="switchCaptionLineDataLabels">
            Show labels for each data point in line chart.
          </span>
        </div>
        <ToggleSwitch
          size="small"
          aria-labelledby="switchLabelLineDataLabels"
          aria-describedby="switchCaptionLineDataLabels"
          checked={chartCardLineDataLabels}
          onClick={() => {
            setChartCardLineDataLabels(!chartCardLineDataLabels)
          }}
        />
      </div>
    </div>
  )
}

const CustomizationDialog: React.FC<{chartType?: string; onCloseCallback: () => void}> = ({
  chartType,
  onCloseCallback,
}) => {
  return (
    chartType && (
      <Dialog
        title={['column', 'bar'].includes(chartType) ? 'Column & bar chart settings' : 'Line chart settings'}
        onClose={onCloseCallback}
        renderBody={() => (
          <DialogContentBody type={['column', 'bar'].includes(chartType) ? 'ColumnChart' : 'LineChart'} />
        )}
      />
    )
  )
}

export default CustomizationDialog

try{ DialogContentBody.displayName ||= 'DialogContentBody' } catch {}
try{ CustomizationDialog.displayName ||= 'CustomizationDialog' } catch {}